import { useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { plans, waves, projects, migrationReport, WavesPreviewResponse } from '@/api/client'
import { ArrowLeft, Loader2, AlertTriangle, CheckCircle2, Clock, Users, HardDrive, Zap, Home, Server, Lock } from 'lucide-react'
import { StrategyPicker } from '@/components/StrategyPicker'

type ExecMode = 'Automatic' | 'Manual' | 'Scheduled'

function formatBytes(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return gb.toFixed(1) + ' GB'
  const mb = bytes / (1024 * 1024)
  return mb.toFixed(0) + ' MB'
}

function formatDuration(min: number): string {
  if (min >= 60) return `${Math.floor(min / 60)}h ${min % 60}m`
  return `${min}m`
}

export function ReviewApprovalPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projects.get(projectId!),
    enabled: !!projectId,
  })
  const planQuery = useQuery({
    queryKey: ['plan', projectId],
    queryFn: () => plans.get(projectId!),
    enabled: !!projectId,
  })
  const previewQuery = useQuery<WavesPreviewResponse>({
    queryKey: ['waves-preview', projectId],
    queryFn: () => waves.preview(projectId!),
    enabled: !!projectId,
  })

  const [mode, setMode] = useState<ExecMode>('Automatic')
  const [scheduledStart, setScheduledStart] = useState('')
  const [overrides, setOverrides] = useState<Record<number, number>>({})

  const setProductionSide = useMutation({
    mutationFn: (side: 'Origem' | 'Destino') => migrationReport.setProductionSide(projectId!, side),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
  })

  const approve = useMutation({
    mutationFn: () => waves.approve(projectId!, {
      executionMode: mode,
      scheduledStartAt: mode === 'Scheduled' && scheduledStart ? new Date(scheduledStart).toISOString() : null,
      waveOverrides: Object.entries(overrides).map(([order, parallelism]) => ({
        order: parseInt(order),
        parallelism,
      })),
    }),
    onSuccess: () => navigate(`/projects/${projectId}`),
  })

  if (!projectId) return null

  if (planQuery.isLoading || previewQuery.isLoading) {
    return (
      <div className="card p-8 text-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
        Calculando ondas...
      </div>
    )
  }

  const plan = planQuery.data
  const preview = previewQuery.data
  const project = projectQuery.data

  if (!plan || !preview || !project) {
    return <div className="text-red-700">Erro ao carregar dados.</div>
  }

  const isApproved = plan.status !== 'Draft'
  const usersMissingLicense = plan.summary.selectedWithoutLicense ?? 0  // Sprint 4f Bug 38
  const canApprove = !isApproved
    && plan.summary.selectedUsers > 0
    && usersMissingLicense === 0
  const hasDeficits = Object.keys(plan.summary.licenseDeficits).length > 0

  // Produção Origem/Destino: editável só antes da execução começar.
  const prodLocked = ['Executing', 'Auditing', 'Cutover', 'Completed'].includes(project.status)
  const currentSide: 'Origem' | 'Destino' = setProductionSide.isPending && setProductionSide.variables
    ? setProductionSide.variables
    : (project.productionSide === 'Destino' ? 'Destino' : 'Origem')

  return (
    <div className="max-w-5xl">
      <Link to={`/projects/${projectId}/plan`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar ao planejamento
      </Link>

      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Revisar e aprovar</h1>
      <p className="text-sm text-slate-500 mb-6">
        Confirme as ondas geradas, escolha o modo de execução e aprove o plano.
        {' '}
        <strong>Após aprovação, plano vira imutável.</strong>
      </p>

      {/* Cards summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="card p-3">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" /> Usuários</div>
          <div className="text-2xl font-semibold text-slate-900">{preview.totalUsers}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-slate-500 flex items-center gap-1"><HardDrive className="h-3 w-3" /> Volume</div>
          <div className="text-2xl font-semibold text-slate-900">{formatBytes(preview.totalBytes)}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Tempo estimado</div>
          <div className="text-2xl font-semibold text-slate-900">{formatDuration(preview.estimatedTotalMinutes)}</div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-slate-500 flex items-center gap-1"><Zap className="h-3 w-3" /> Ondas</div>
          <div className="text-2xl font-semibold text-slate-900">{preview.waves.length}</div>
        </div>
      </div>

      {/* Sprint 4f Fase 5: Strategy Picker */}
      <div className="card p-4 mb-6">
        <StrategyPicker
          projectId={projectId}
          currentStrategyId={plan.strategyId}
          readonly={isApproved}
        />
      </div>

      {/* Alerta licenças */}
      {hasDeficits && (
        <div className="rounded-md bg-orange-50 border border-orange-200 p-3 mb-4 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
            <div>
              <strong className="text-orange-900">Atenção: licenças insuficientes no destino</strong>
              <p className="text-orange-800 mt-1 text-xs">
                Você pode aprovar mesmo assim — a execução vai pausar quando esgotar e enviar alerta para você comprar mais licenças.
                Consulte o detalhamento na tela de planejamento.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabela de ondas */}
      <div className="card overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="p-2 text-left text-xs font-medium text-slate-600">#</th>
              <th className="p-2 text-left text-xs font-medium text-slate-600">Onda</th>
              <th className="p-2 text-right text-xs font-medium text-slate-600">Usuários</th>
              <th className="p-2 text-right text-xs font-medium text-slate-600">Volume</th>
              <th className="p-2 text-right text-xs font-medium text-slate-600">Tempo</th>
              <th className="p-2 text-right text-xs font-medium text-slate-600">
                Paralelismo por conta
                <span className="block font-normal text-[10px] text-slate-400">contas simultâneas</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {preview.waves.map(w => (
              <tr key={w.order}>
                <td className="p-2 text-slate-500">{w.order}</td>
                <td className="p-2">
                  <div className="font-medium">{w.name}</div>
                </td>
                <td className="p-2 text-right">{w.userCount}</td>
                <td className="p-2 text-right text-slate-600">{formatBytes(w.bytes)}</td>
                <td className="p-2 text-right text-slate-600">{formatDuration(w.estimatedMinutes)}</td>
                <td className="p-2 text-right">
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className="input !py-1 !text-xs !min-h-0 w-16 text-right"
                    value={overrides[w.order] ?? w.suggestedParallelism}
                    onChange={(e) => setOverrides(prev => ({ ...prev, [w.order]: parseInt(e.target.value) || 1 }))}
                    disabled={isApproved}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 -mt-4 mb-6">
        <strong>Paralelismo por conta</strong> (contas simultâneas, acima) × <strong>paralelismo por mensagem</strong>{' '}
        (mensagens simultâneas por conta, na estratégia) = total de operações EWS simultâneas na origem.
        Valores altos aceleram, mas a Microsoft pode retaliar a origem (throttle/401) — prefira escalar contas, não mensagens.
      </p>

      {/* Lado de produção (Sprint 9) */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-slate-900">Lado de produção</h3>
          {prodLocked && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <Lock className="h-3 w-3" /> Travado após início da execução
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Define quem é a fonte da verdade. Só pode ser alterado <strong>antes</strong> de iniciar a migração.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <SideOption
            checked={currentSide === 'Origem'}
            onChange={() => setProductionSide.mutate('Origem')}
            disabled={prodLocked || setProductionSide.isPending}
            icon={<Home className="h-4 w-4" />}
            title="Origem (padrão)"
            description="A origem é a fonte da verdade; o destino é espelho fiel. Migração completa e espelho ligado (move/delete na origem refletem no destino)."
          />
          <SideOption
            checked={currentSide === 'Destino'}
            onChange={() => setProductionSide.mutate('Destino')}
            disabled={prodLocked || setProductionSide.isPending}
            icon={<Server className="h-4 w-4" />}
            title="Destino"
            description="O destino já está em produção. Migração só aditiva, com dedupe global; o espelho é desligado por segurança."
          />
        </div>
        {setProductionSide.isError && (
          <p className="text-xs text-red-700 mt-2">
            {(setProductionSide.error as any)?.message || 'Erro ao alterar lado de produção'}
          </p>
        )}
      </div>

      {/* Modo de execução */}
      <div className="card p-4 mb-6">
        <h3 className="text-sm font-medium text-slate-900 mb-3">Modo de execução</h3>
        <div className="space-y-2">
          <ModeOption
            checked={mode === 'Automatic'}
            onChange={() => setMode('Automatic')}
            title="Automático (recomendado)"
            description="As ondas executam em sequência, sem precisar de você. Falhas individuais não bloqueiam — ficam registradas e você revisa depois."
            disabled={isApproved}
          />
          <ModeOption
            checked={mode === 'Scheduled'}
            onChange={() => setMode('Scheduled')}
            title="Agendado"
            description="Inicia execução automática em data/hora específica."
            disabled={isApproved}
          />
          {mode === 'Scheduled' && (
            <div className="ml-7 mt-2">
              <input
                type="datetime-local"
                className="input !py-1.5 !text-xs"
                value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
                disabled={isApproved}
              />
            </div>
          )}
          <ModeOption
            checked={mode === 'Manual'}
            onChange={() => setMode('Manual')}
            title="Manual (onda por onda)"
            description="Você libera cada onda individualmente. Útil para validar piloto antes do massivo."
            disabled={isApproved}
          />
        </div>
      </div>

      {isApproved ? (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div className="flex-1">
              <strong className="text-emerald-900">Plano aprovado em {plan.approvedAt ? new Date(plan.approvedAt).toLocaleString('pt-BR') : ''}</strong>
              <p className="text-sm text-emerald-800 mt-1">Modo: {plan.executionMode}.</p>
            </div>
            <Link to={`/projects/${projectId}/execute`} className="btn-primary">
              Executar migração →
            </Link>
          </div>
        </div>
      ) : (
        <>
          {approve.isError && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 mb-4 text-sm text-red-800">
              {(approve.error as any)?.message || 'Erro ao aprovar'}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Link to={`/projects/${projectId}/plan`} className="btn-secondary">
              Voltar e ajustar
            </Link>
            <button
              onClick={() => approve.mutate()}
              disabled={!canApprove || approve.isPending}
              className="btn-primary"
            >
              {approve.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Aprovar plano e gerar ondas
            </button>
          </div>
          {!canApprove && !isApproved && (
            <p className="text-xs mt-2 text-right">
              {plan.summary.selectedUsers === 0 ? (
                <span className="text-slate-500">
                  Selecione pelo menos 1 usuário no planejamento para aprovar.
                </span>
              ) : usersMissingLicense > 0 ? (
                <span className="text-orange-700 font-medium">
                  {usersMissingLicense} {usersMissingLicense === 1 ? 'usuário criado precisa' : 'usuários criados precisam'} de licença atribuída.
                  Volte ao planejamento e selecione uma licença para cada um.
                </span>
              ) : null}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function SideOption({ checked, onChange, title, description, disabled, icon }: {
  checked: boolean
  onChange: () => void
  title: string
  description: string
  disabled?: boolean
  icon: ReactNode
}) {
  return (
    <label className={`flex items-start gap-3 p-3 border rounded-md ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${checked ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-0.5"
      />
      <div>
        <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">{icon}{title}</div>
        <div className="text-xs text-slate-600 mt-0.5">{description}</div>
      </div>
    </label>
  )
}

function ModeOption({ checked, onChange, title, description, disabled }: {
  checked: boolean
  onChange: () => void
  title: string
  description: string
  disabled?: boolean
}) {
  return (
    <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${checked ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-0.5"
      />
      <div>
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <div className="text-xs text-slate-600 mt-0.5">{description}</div>
      </div>
    </label>
  )
}
